/******************************************************************************
 * FILE:
 * src/modules/expenseManagement/workflow/pages/WorkflowListPage.jsx
 *
 * MODULE:
 * Expense Management
 *
 * FEATURE:
 * Workflow Master
 *
 * PURPOSE:
 * Workflow List Page
 *
 ******************************************************************************/

import React, {
    useEffect,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";

import WorkflowHeader from "../components/WorkflowHeader";
import WorkflowToolbar from "../components/WorkflowToolbar";
import WorkflowTable from "../components/WorkflowTable";
import WorkflowDrawer from "../components/WorkflowDrawer";

import useWorkflowMaster from "../hooks/useWorkflowMaster";

export default function WorkflowListPage() {

    const navigate = useNavigate();

    const {

    loading,

    saving,

    workflows,

    workflowTypes,

    departments,

    loadWorkflows,

    loadWorkflowTypes,

    loadDepartments,

    createWorkflow,

    updateWorkflow,

    deleteWorkflow,

    activateWorkflow,

    deactivateWorkflow,

} = useWorkflowMaster();

    /**
     * ------------------------------------------------------------------
     * Drawer State
     * ------------------------------------------------------------------
     */

    const [drawerOpen, setDrawerOpen] =
        useState(false);

    const [drawerMode, setDrawerMode] =
        useState("create");

    const [selectedWorkflow, setSelectedWorkflow] =
        useState(null);

    /**
     * ------------------------------------------------------------------
     * Filters
     * ------------------------------------------------------------------
     */

    const [filters, setFilters] =
        useState({

            search: "",

            workflow_type: "",

            is_active: "",

        });

    /**
     * ------------------------------------------------------------------
     * Initial Form Data
     * ------------------------------------------------------------------
     */

    const initialFormData = {

        code: "",

        name: "",

        workflow_type: "",

        department_id: "",

        version_no: 1,

        effective_from: "",

        effective_to: "",

        description: "",

        remarks: "",

        is_default: false,

        is_active: true,

    };

    /**
     * ------------------------------------------------------------------
     * Form State
     * ------------------------------------------------------------------
     */

    const [formData, setFormData] =
        useState(initialFormData);

    const [errors, setErrors] =
        useState({});

    /**
     * ------------------------------------------------------------------
     * Load Workflow List
     * ------------------------------------------------------------------
     */

    const loadData = async () => {

        await Promise.all([

            loadWorkflows(filters),

            loadWorkflowTypes(),

            loadDepartments(),

        ]);

    };

    /**
     * ------------------------------------------------------------------
     * Initial Load
     * ------------------------------------------------------------------
     */

    useEffect(() => {

        loadData();

    }, []);

    /**
     * ------------------------------------------------------------------
     * Filter Change
     * ------------------------------------------------------------------
     */

    const handleFilterChange = (

        field,

        value

    ) => {

        setFilters((prev) => ({

            ...prev,

            [field]: value,

        }));

    };

    /**
     * ------------------------------------------------------------------
     * Search
     * ------------------------------------------------------------------
     */

    const handleSearch = () => {

        loadData();

    };

    /**
     * ------------------------------------------------------------------
     * Reset Filters
     * ------------------------------------------------------------------
     */

    const handleReset = () => {

        setFilters({

            search: "",

            workflow_type: "",

            is_active: "",

        });

    };

    /**
     * ------------------------------------------------------------------
     * Refresh
     * ------------------------------------------------------------------
     */

    const handleRefresh = () => {

        loadData();

    };

    /**
     * ------------------------------------------------------------------
     * Reset Form
     * ------------------------------------------------------------------
     */

    const resetForm = () => {

        setFormData(initialFormData);

        setErrors({});

        setSelectedWorkflow(null);

        setDrawerMode("create");

    };

    /**
     * ------------------------------------------------------------------
     * Create Workflow
     * ------------------------------------------------------------------
     */

    const handleCreate = () => {

        resetForm();

        setDrawerOpen(true);

    };

    /**
     * ------------------------------------------------------------------
     * Edit Workflow
     * ------------------------------------------------------------------
     */

    const handleEdit = (workflow) => {

        setDrawerMode("edit");

        setSelectedWorkflow(workflow);

        setFormData({

            ...initialFormData,

            ...workflow,

        });

        setErrors({});

        setDrawerOpen(true);

    };

    /**
     * ------------------------------------------------------------------
     * Close Drawer
     * ------------------------------------------------------------------
     */

    const handleDrawerClose = () => {

        resetForm();

        setDrawerOpen(false);

    };

    /**
     * ------------------------------------------------------------------
     * Form Change
     * ------------------------------------------------------------------
     */

    const handleFormChange = (

        field,

        value

    ) => {

        setFormData((prev) => ({

            ...prev,

            [field]: value,

        }));

    };
        /**
     * ------------------------------------------------------------------
     * Save Workflow
     * ------------------------------------------------------------------
     */

    const handleSubmit = async (event) => {

        event.preventDefault();

        const validationErrors = {};

        if (!formData.code?.trim()) {

            validationErrors.code =
                "Workflow Code is required.";

        }

        if (!formData.name?.trim()) {

            validationErrors.name =
                "Workflow Name is required.";

        }

        if (!formData.workflow_type) {

            validationErrors.workflow_type =
                "Workflow Type is required.";

        }

        if (Object.keys(validationErrors).length > 0) {

            setErrors(validationErrors);

            return;

        }

        try {

            setErrors({});

            if (drawerMode === "create") {

                await createWorkflow(formData);

            } else {

                await updateWorkflow(

                    selectedWorkflow.workflow_id,

                    formData

                );

            }

            handleDrawerClose();

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------------
     * Delete Workflow
     * ------------------------------------------------------------------
     */

    const handleDelete = async (

        workflow

    ) => {

        if (

            !window.confirm(

                "Are you sure you want to delete this workflow?"

            )

        ) {

            return;

        }

        try {

            await deleteWorkflow(

                workflow.workflow_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------------
     * Activate Workflow
     * ------------------------------------------------------------------
     */

    const handleActivate = async (

        workflow

    ) => {

        try {

            await activateWorkflow(

                workflow.workflow_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------------
     * Deactivate Workflow
     * ------------------------------------------------------------------
     */

    const handleDeactivate = async (

        workflow

    ) => {

        try {

            await deactivateWorkflow(

                workflow.workflow_id

            );

            loadData();

        } catch (error) {

            console.error(error);

        }

    };

    /**
     * ------------------------------------------------------------------
     * Open Workflow
     * ------------------------------------------------------------------
     */

    const handleOpen = (

    workflow

) => {

    navigate(

        `/expense-management/configuration/approval-workflow/${workflow.workflow_id}`

    );

};

    /**
     * ------------------------------------------------------------------
     * Render
     * ------------------------------------------------------------------
     */

    return (

        <>

            <WorkflowHeader

                title="Approval Workflow"

                subtitle="Configure Expense Approval Workflows"

                actions={

                    <button

                        className="btn btn-primary"

                        onClick={handleCreate}

                    >

                        + New Workflow

                    </button>

                }

            />

            <WorkflowToolbar

                filters={filters}

                onFilterChange={handleFilterChange}

                onSearch={handleSearch}

                onReset={handleReset}

                onRefresh={handleRefresh}

                onCreate={handleCreate}

            />

            <WorkflowTable

                loading={loading}

                workflows={workflows}

                onOpen={handleOpen}

                onEdit={handleEdit}

                onDelete={handleDelete}

                onActivate={handleActivate}

                onDeactivate={handleDeactivate}

            />

            <WorkflowDrawer

                open={drawerOpen}

                mode={drawerMode}

                workflow={selectedWorkflow}

                formData={formData}

                errors={errors}

                loading={loading}

                saving={saving}

                workflowTypes={workflowTypes}

                departments={departments}

                onChange={handleFormChange}

                onSubmit={handleSubmit}

                onClose={handleDrawerClose}

            />

        </>

    );

}